import { Alert, Clickable, List } from '@ahmdigital/ui';
import { createRef, RefObject, useEffect } from 'react';
import { isEmpty, map } from 'lodash/fp';
import { PRODUCT_TYPE } from '@ahmdigital/constants';
import { useSelector } from 'react-redux';
import styled from 'styled-components';

import { BuildYourCoverProductType } from '../../../../../../actions/build-your-cover';
import { ProductTypeType } from '../../../../../../types/product';
import { scrollToElement } from '../../../../../../utils/ui';
import { useByocNavigationContext } from '../../../../context/byoc-navigation-context';
import getCarouselTypeOrder from '../../../../utils/get-carousel-type-order';
import selectBuildYourCover from '../../../../../../selectors/select-build-your-cover';

const BoldClickable = styled(Clickable)`
  font-weight: ${({ theme }) => theme.fontWeights.bold};
`;

type ValidationAlertProps = {
  hospitalSectionRef: RefObject<HTMLDivElement>;
  extrasSectionRef: RefObject<HTMLDivElement>;
  addOnType: BuildYourCoverProductType | null;
};

const ValidationAlert = ({ hospitalSectionRef, extrasSectionRef, addOnType }: ValidationAlertProps) => {
  const { invalidProductTypes } = useSelector(selectBuildYourCover);
  const { journeyType } = useByocNavigationContext();
  const errorRef = createRef<HTMLDivElement>();

  useEffect(() => {
    if (!isEmpty(invalidProductTypes) && errorRef.current) {
      scrollToElement(errorRef.current);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invalidProductTypes]);

  if (isEmpty(invalidProductTypes)) {
    return null;
  }

  const scrollToError = (productType: ProductTypeType) => {
    scrollToElement((productType === 'hospital' ? hospitalSectionRef : extrasSectionRef).current);
  };

  // / Order of errors should match order of carousels
  const orderedProductType = getCarouselTypeOrder(addOnType);
  const productTypeToValidate = journeyType === PRODUCT_TYPE.PACKAGE ? orderedProductType : [journeyType];
  const filteredProductType = productTypeToValidate.filter((productType) => invalidProductTypes.includes(productType));

  if (isEmpty(filteredProductType)) {
    return null;
  }

  return (
    <div ref={errorRef}>
      <Alert heading="Please select your cover to continue." level="danger">
        <List variant={['inside']}>
          {map(
            (productType) => (
              <li key={`choose-your-cover-error-missing-${productType}`}>
                Select <BoldClickable onClick={() => scrollToError(productType)}>{productType} cover</BoldClickable>
              </li>
            ),
            filteredProductType,
          )}
        </List>
      </Alert>
    </div>
  );
};

export default ValidationAlert;
